import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, Phone, Instagram, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useT, useUI } from "../lib/i18n";
import SEO from "../components/site/SEO";
import BookingWidget from "../components/site/BookingWidget";

export default function Contact() {
  const { content } = useOutletContext();
  const t = useT();
  const ui = useUI();
  const info = content.contact_info || {};
  const seo = (content.seo || {}).contact;
  const showBooking = (content.sections_visibility || {}).booking_form !== false;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: "" });
    try {
      await api.post("/contact", form);
      setStatus({ loading: false, success: true, error: "" });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ loading: false, success: false, error: typeof detail === "string" ? detail : "Une erreur est survenue." });
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO seoData={seo} />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 grid md:grid-cols-5 gap-16">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="md:col-span-2">
          <p className="overline mb-4">{ui.contactLabel}</p>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter leading-none font-light">{t(info.title)}</h1>
          <p className="mt-6 text-[#4A3F35] leading-relaxed">{t(info.description)}</p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F6F3EC] flex items-center justify-center text-[#C87A5E] shrink-0"><Mail size={18} /></div>
              <div><div className="overline">{ui.email}</div><a href={`mailto:${info.email}`} className="link-underline text-[#2D2A26]">{info.email}</a></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F6F3EC] flex items-center justify-center text-[#C87A5E] shrink-0"><Phone size={18} /></div>
              <div><div className="overline">{ui.phone}</div><div>{info.phone}</div></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F6F3EC] flex items-center justify-center text-[#C87A5E] shrink-0"><MapPin size={18} /></div>
              <div><div className="overline">{ui.location}</div><div>{t(info.location)}</div></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F6F3EC] flex items-center justify-center text-[#C87A5E] shrink-0"><Instagram size={18} /></div>
              <div><div className="overline">{ui.instagram}</div><div>{info.instagram}</div></div>
            </div>
          </div>
        </motion.div>

        <motion.form initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} onSubmit={onSubmit} className="md:col-span-3 bg-[#F6F3EC] rounded-3xl p-8 md:p-12" data-testid="contact-form">
          {status.success ? (
            <div className="flex flex-col items-center text-center py-12" data-testid="contact-success">
              <CheckCircle2 size={56} className="text-[#8A9A86] mb-5" />
              <h3 className="font-serif text-3xl">{ui.messageSent}</h3>
              <p className="text-[#4A3F35] mt-3 max-w-md">{ui.thanksForTrust}</p>
              <button onClick={() => setStatus({ loading: false, success: false, error: "" })} className="btn-ghost mt-8">{ui.sendAnother}</button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={ui.yourName} name="name" value={form.name} onChange={handleChange} required />
                <Field label={ui.email} type="email" name="email" value={form.email} onChange={handleChange} required />
                <Field label={ui.phone} name="phone" value={form.phone} onChange={handleChange} />
                <div>
                  <label className="overline block mb-2">{ui.sessionType}</label>
                  <select name="service" value={form.service} onChange={handleChange} className="w-full bg-transparent border-b border-[#2D2A26]/30 py-2.5 focus:border-[#C87A5E] outline-none transition">
                    <option value="">{ui.chooseOption}</option>
                    <option value="Familiale">{ui.famille}</option>
                    <option value="Couple">{ui.couple}</option>
                    <option value="Naissance">{ui.naissance}</option>
                    <option value="Grossesse">{ui.grossesse}</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="overline block mb-2">{ui.yourMessage}</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={5} required className="w-full bg-transparent border-b border-[#2D2A26]/30 py-2.5 focus:border-[#C87A5E] outline-none transition resize-none" />
              </div>
              {status.error && <div className="mt-5 text-sm text-red-700">{status.error}</div>}
              <button type="submit" disabled={status.loading} className="btn-primary mt-8 disabled:opacity-50" data-testid="contact-submit">
                {status.loading ? ui.sending : ui.sendMessage} <Send size={16} />
              </button>
            </>
          )}
        </motion.form>
      </section>

      {showBooking && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="booking-section">
          <BookingWidget content={content} />
        </section>
      )}
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input {...props} className="w-full bg-transparent border-b border-[#2D2A26]/30 py-2.5 focus:border-[#C87A5E] outline-none transition" />
    </div>
  );
}
